/**
 * Debug index: when not admin, shows "Become admin (dev only)" so the debug panel (gear) becomes visible.
 *
 * CRITICAL FIX: Always render the same Container structure to prevent hydration mismatches.
 * Unlike PlayGameRoute which conditionally renders content inside Container, we were changing
 * the Container structure itself (different className, different children structure).
 * Now we always render the same Container structure and only change content inside.
 */

import React from 'react';

import { useAccount, useSetUserRole } from '~/apis';
import { DEBUG_enabled, isProductionEnvironment } from '~/app';
import { useAuthState } from '~/auth';
import Container from '~/components/Container';
import LoadingSpinner from '~/components/LoadingSpinner/LoadingSpinner';
import { UserRole } from '~/server/src/dtos';

const DebugIndex = (): JSX.Element => {
  const { isAuthorized, state: authState } = useAuthState();
  const { data: account, refetch: refetchAccount } = useAccount();
  const setUserRoleMutation = useSetUserRole();

  const canBecomeAdmin =
    DEBUG_enabled &&
    !isProductionEnvironment &&
    isAuthorized &&
    account &&
    account.role !== UserRole.Admin;
  const isAdmin = account?.role === UserRole.Admin;

  const handleBecomeAdmin = () => {
    if (!account?.userId) return;
    setUserRoleMutation.mutate(
      { userId: account.userId, role: UserRole.Admin },
      { onSuccess: () => refetchAccount() },
    );
  };

  // Always render the same Container structure - never change the structure itself
  // This prevents hydration mismatches at the Router level
  // Only the content inside changes based on state, similar to PlayGameRoute
  const isLoading =
    authState === 'initializing' ||
    (isAuthorized && account === undefined && !setUserRoleMutation.isError);

  return (
    <Container header="Debug" className="max-w-3xl space-y-4 px-4 pb-16">
      {isLoading ? (
        <div className="flex min-h-[50vh] items-center justify-center">
          <LoadingSpinner />
        </div>
      ) : !isAuthorized ? (
        <div className="flex flex-col space-y-6 rounded-md bg-white p-4 shadow-md">
          <div className="flex flex-col items-stretch space-y-4">
            <p className="text-gray-700">Log in to use debug features.</p>
          </div>
        </div>
      ) : (
        <>
          {canBecomeAdmin && (
            <div className="flex flex-col space-y-6 rounded-md bg-white p-4 shadow-md">
              <div className="flex flex-col items-stretch space-y-4">
                <h3 className="text-xl font-bold">Become Admin</h3>
                <p className="text-gray-700">
                  In development you can grant yourself admin to see the debug panel (gear icon) and
                  React Query DevTools.
                </p>
                <button
                  type="button"
                  onClick={handleBecomeAdmin}
                  disabled={setUserRoleMutation.isPending}
                  className="w-auto rounded-md bg-[#405820] px-4 py-2 font-medium text-white hover:bg-[#405820]/90 focus:ring-2 focus:ring-[#405820]/50 focus:outline-none disabled:opacity-50"
                >
                  {setUserRoleMutation.isPending ? 'Setting role…' : 'Become admin (dev only)'}
                </button>
                {setUserRoleMutation.isSuccess && (
                  <p className="text-sm text-green-700">
                    Role set. Reload the page to see the debug panel.
                  </p>
                )}
                {setUserRoleMutation.isError && (
                  <p className="text-sm text-red-600">
                    Failed:{' '}
                    {(setUserRoleMutation.error as { message?: string })?.message ??
                      'unknown error'}
                  </p>
                )}
              </div>
            </div>
          )}
          {isAdmin && (
            <div className="flex flex-col space-y-6 rounded-md bg-white p-4 shadow-md">
              <div className="flex flex-col items-stretch space-y-4">
                <h3 className="text-xl font-bold">Admin Access</h3>
                <p className="text-gray-700">
                  You are an admin. Use the <strong>gear icon</strong> on the left edge of the
                  screen to open the debug panel (Admin Controls, etc.).
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </Container>
  );
};

export default DebugIndex;
